import { useEffect, useMemo, useState } from "react";
import { useAppStore } from "lib/store/useAppStore";
import { formatCountryLabel } from "lib/utils/countryLabels";
import { formatSystemRole } from "lib/utils/roleLabels";

const ROLE_RANK = { user: 1, manager: 2, admin: 3, master: 4 };
const DEFAULT_COUNTRIES = [
  { code: "KR", default_name: "Korea" },
  { code: "US", default_name: "United States" },
  { code: "VN", default_name: "Vietnam" },
  { code: "JP", default_name: "Japan" },
  { code: "CN", default_name: "China" },
];

function getAllowedRolesByActor(actorRole = "user") {
  if (actorRole === "master") return ["user", "manager", "admin", "master"];
  if (actorRole === "admin") return ["user", "manager"];
  if (actorRole === "manager") return ["user"];

  const actorRank = ROLE_RANK[actorRole] ?? 1;
  // ✅ actorRank 이하만 노출 (리스트에서 제거)
  return ["user", "manager", "admin", "master"].filter(
    (r) => (ROLE_RANK[r] ?? 1) <= actorRank,
  );
}

// ✅ 허용된 role 중 가장 높은 권한(=rank가 가장 큰 것) 반환
function getMaxAllowedRole(allowedRoles = []) {
  if (!allowedRoles?.length) return "user";
  return allowedRoles.reduce((best, cur) =>
    (ROLE_RANK[cur] ?? 1) > (ROLE_RANK[best] ?? 1) ? cur : best,
  );
}

function normalizeCountryOption(country) {
  const code = country?.code ?? country?.country_code ?? "";
  if (!code) return null;
  return {
    ...country,
    code,
    default_name:
      country?.default_name ?? country?.country_name ?? country?.name ?? code,
  };
}

export default function AdminUserCreateModal({
  open,
  onClose,
  onCreate,
  defaultRole = "user",
  countries = [],
}) {
  const me = useAppStore((s) => s.user);
  const actorRole = me?.role ?? "user";

  // ✅ 로그인 유저 기준 허용 role 목록
  const allowedRoles = useMemo(
    () => getAllowedRolesByActor(actorRole),
    [actorRole],
  );

  const fallbackRole = useMemo(
    () => getMaxAllowedRole(allowedRoles),
    [allowedRoles],
  );

  const countryOptions = useMemo(() => {
    const source = Array.isArray(countries) && countries.length
      ? countries
      : DEFAULT_COUNTRIES;

    return source.map(normalizeCountryOption).filter(Boolean);
  }, [countries]);

  const defaultCountryCode = useMemo(() => {
    const currentCountryCode = me?.country_code ?? "";
    if (
      currentCountryCode &&
      countryOptions.some((country) => country.code === currentCountryCode)
    ) {
      return currentCountryCode;
    }

    return countryOptions[0]?.code ?? "";
  }, [countryOptions, me?.country_code]);

  const [form, setForm] = useState({
    email: "",
    user_name: "",
    password: "",
    role: defaultRole,
    country_code: "",
  });

  const [errors, setErrors] = useState({});

  // ✅ open 될 때마다 초기화 + role 보정
  useEffect(() => {
    if (!open) return;

    // defaultRole이 허용되지 않으면 fallback으로 보정
    const initialRole = allowedRoles.includes(defaultRole)
      ? defaultRole
      : fallbackRole;

    setForm({
      email: "",
      user_name: "",
      password: "",
      role: initialRole,
      country_code: defaultCountryCode,
    });
    setErrors({});
  }, [open, defaultRole, allowedRoles, fallbackRole, defaultCountryCode]);

  // ✅ actorRole이 바뀌거나 allowedRoles가 바뀌었을 때 현재 선택 role 보정
  useEffect(() => {
    if (!open) return;
    setForm((prev) => {
      const next = { ...prev };

      if (!next?.role) next.role = fallbackRole;
      else if (!allowedRoles.includes(next.role)) next.role = fallbackRole;

      const countryCodes = countryOptions.map((country) => country.code);
      if (!countryCodes.includes(next.country_code)) {
        next.country_code = defaultCountryCode;
      }

      if (
        next.role === prev.role &&
        next.country_code === prev.country_code
      ) {
        return prev;
      }

      return next;
    });
  }, [
    open,
    allowedRoles,
    countryOptions,
    defaultCountryCode,
    fallbackRole,
  ]);

  // ✅ ESC 닫기
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const canSubmit = useMemo(() => {
    return (
      form.email.trim() &&
      form.user_name.trim() &&
      form.password.trim() &&
      form.role &&
      form.country_code
    );
  }, [form]);

  const validate = () => {
    const next = {};

    const email = form.email.trim();
    if (!email) next.email = "이메일을 입력해 주세요.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      next.email = "이메일 형식이 올바르지 않습니다.";

    if (!form.user_name.trim()) next.user_name = "사용자 이름을 입력해 주세요.";
    if (!form.password.trim()) next.password = "초기 비밀번호를 입력해 주세요.";

    // ✅ role 유효성: 허용되지 않는 role이면 에러
    if (!form.role) next.role = "권한을 선택해 주세요.";
    else if (!allowedRoles.includes(form.role))
      next.role = "이 권한을 부여할 수 없습니다.";

    const countryCodes = countryOptions.map((country) => country.code);
    if (!form.country_code) next.country_code = "국가를 선택해 주세요.";
    else if (!countryCodes.includes(form.country_code)) {
      next.country_code = "선택할 수 없는 국가입니다.";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleChange = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const generateTempPassword = () => {
    const p = `CL-${Math.random().toString(36).slice(2, 8)}-${Math.random()
      .toString(36)
      .slice(2, 6)}`;
    setForm((prev) => ({ ...prev, password: p }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    onCreate?.({
      email: form.email.trim(),
      user_name: form.user_name.trim(),
      password: form.password,
      role: form.role,
      country_code: form.country_code,
    });
  };

  if (!open) return null;

  // ✅ admin/user는 master 생성 옵션 자체가 없음 → 안내 문구(선택)
  const roleHint =
    actorRole === "admin" && form.role === "manager"
      ? "해당 권한을 가진 계정 생성은 운영진의 승인이 필요합니다."
      : "";
  const submitLabel =
    actorRole === "admin" && form.role === "manager"
      ? "매니저 생성 요청"
      : "사용자 생성";

  return (
    <div className="createUserModal" onMouseDown={onClose}>
      <div
        className="adminModal"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="modalHeader">
          <div className="titleBox">
            <h3>사용자 추가</h3>
            <p className="muted">사용자 계정을 만들고 권한을 설정합니다.</p>
          </div>

          <button className="iconBtn" onClick={onClose} type="button">
            ✕
          </button>
        </div>

        <form className="modalBody" onSubmit={handleSubmit}>
          <div className="formGrid">
            <div className="field">
              <p className="label">이메일 (로그인 ID)</p>
              <input
                className={`input ${errors.email ? "isError" : ""}`}
                value={form.email}
                onChange={handleChange("email")}
                placeholder="example@g-mail.com"
                autoComplete="off"
              />
              {errors.email && <p className="errorText">{errors.email}</p>}
            </div>

            <div className="field">
              <p className="label">사용자 이름</p>
              <input
                className={`input ${errors.user_name ? "isError" : ""}`}
                value={form.user_name}
                onChange={handleChange("user_name")}
                placeholder="사용자 이름"
                autoComplete="off"
              />
              {errors.user_name && (
                <p className="errorText">{errors.user_name}</p>
              )}
            </div>

            <div className="field">
              <div className="labelRow">
                <p className="label">권한</p>
              </div>

              <select
                className={`select ${errors.role ? "isError" : ""}`}
                value={form.role}
                onChange={handleChange("role")}
              >
                {/* ✅ 허용된 role만 "아예 렌더" */}
                {allowedRoles.includes("user") && (
                  <option value="user">{formatSystemRole("user")}</option>
                )}
                {allowedRoles.includes("manager") && (
                  <option value="manager">{formatSystemRole("manager")}</option>
                )}
                {allowedRoles.includes("admin") && (
                  <option value="admin">{formatSystemRole("admin")}</option>
                )}
                {allowedRoles.includes("master") && (
                  <option value="master">{formatSystemRole("master")}</option>
                )}
              </select>

              {roleHint ? (
                <p className="helpText muted">{roleHint}</p>
              ) : null}

              {errors.role && <p className="errorText">{errors.role}</p>}
            </div>

            <div className="field">
              <div className="labelRow">
                <p className="label">국가</p>
              </div>

              <select
                className={`select ${errors.country_code ? "isError" : ""}`}
                value={form.country_code}
                onChange={handleChange("country_code")}
              >
                {countryOptions.map((country) => (
                  <option key={country.code} value={country.code}>
                    {formatCountryLabel(country)}
                  </option>
                ))}
              </select>

              {errors.country_code && (
                <p className="errorText">{errors.country_code}</p>
              )}
            </div>

            <div className="field">
              <div className="labelRow">
                <p className="label">초기 비밀번호</p>
                <button
                  className="ghostMiniBtn"
                  type="button"
                  onClick={generateTempPassword}
                >
                  임시 비밀번호 생성
                </button>
              </div>

              <input
                className={`input ${errors.password ? "isError" : ""}`}
                value={form.password}
                onChange={handleChange("password")}
                placeholder="초기 비밀번호"
                autoComplete="new-password"
              />
              {errors.password && (
                <p className="errorText">{errors.password}</p>
              )}
            </div>
          </div>

          <div className="modalFooter">
            <button className="ghostBtn" type="button" onClick={onClose}>
              취소
            </button>

            <button className="primaryBtn" type="submit" disabled={!canSubmit}>
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
